import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';
import { consultarCategorias } from '../../../nucleo/api/nucleoApi.js';
import { obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import { CintaDeVersionNueva } from '../../../nucleo/pwa/CintaDeVersionNueva.jsx';
import { generarPlan, obtenerPrestamo, simularPlan } from '../api/prestamosApi.js';
import { FormularioDelPlan } from '../componentes/FormularioDelPlan.jsx';

const VALORES_INICIALES = {
  montoOriginal: '',
  pagoMensual: '',
  numeroDePagos: '',
  pagosRealizados: '',
  fechaDelProximoPago: '',
  idCategoriaDelInteres: '',
};

/**
 * EL PLAN DE PAGOS DEL PRÉSTAMO (RF-265; segunda de las dos llamadas de §1).
 *
 * SE LLEGA AQUÍ POR DOS CAMINOS Y LA PANTALLA ES LA MISMA: recién dado de alta el
 * préstamo (P-20-01) o después, desde un préstamo que se quedó sin plan (P-20-06,
 * D-457). Un solo formulario para los dos: con dos parecidos, la próxima regla del
 * plazo se aplicaría en uno y se olvidaría en el otro.
 *
 * NACEN SOLO LOS PAGOS QUE FALTAN, numerados M+1..N (D-433, RN-741): del pasado se
 * guardan DOS HECHOS que ella declara —cuántos pagos van y cuánto debe hoy— y nada
 * más. Así un préstamo contratado antes de 2025 se registra sin chocar con el piso
 * del calendario.
 *
 * LA VISTA PREVIA LA CALCULA EL SERVIDOR con la MISMA preparación con la que guarda
 * (§2): esta pantalla no reparte importes, no cuenta pagos y no calcula fechas
 * (RNF-038, RNF-138). Y NO VALIDA EL RANGO: el plazo y las fechas los decide el
 * servidor con la maquinaria única y el calendario sembrado (RN-759, RN-763), que es
 * también quien redacta el rechazo con el techo CALCULADO (RN-766). Aquí no hay —ni
 * puede haber— un «360» de adorno ni un «2061» pegado.
 */
export default function GenerarPlanDePagos() {
  const { idCuenta } = useParams();
  const [parametros] = useSearchParams();
  const navegar = useNavigate();
  const hoy = obtenerFechaDeNegocioHoy();

  const recienCreado = parametros.get('recienCreado') === '1';

  const [prestamo, setPrestamo] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [valores, setValores] = useState(() => ({
    ...VALORES_INICIALES,
    montoOriginal: parametros.get('montoOriginal') ?? '',
  }));

  const [vistaPrevia, setVistaPrevia] = useState(null);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [elPrestamo, respuestaDeCategorias] = await Promise.all([
          obtenerPrestamo({ idCuenta: Number(idCuenta), fechaLocal: hoy }),
          // D-436 · la categoría del interés es una categoría de GASTO de su propio
          // catálogo: no se siembra ninguna categoría nueva.
          consultarCategorias({ tipo: 'GASTO', soloActivas: true }),
        ]);

        setPrestamo(elPrestamo);
        setCategorias(respuestaDeCategorias.categorias ?? respuestaDeCategorias);

        // Un préstamo que YA tiene plan no se vuelve a generar: se corrige
        // cancelando y regenerando (RN-754). El servidor responde PLAN_YA_EXISTE;
        // esto evita el viaje y lleva al detalle, que es donde está la acción.
        if (elPrestamo.planGenerado) {
          navegar(`/cuentas/prestamos/${idCuenta}`, { replace: true });
        }
      } catch (fallo) {
        setError(fallo);
      }
    })();
  }, [idCuenta, hoy, navegar]);

  const codigoISO = prestamo?.cuenta?.codigoISO ?? null;

  const datosCompletos = Number(valores.montoOriginal) > 0
    && Number(valores.pagoMensual) > 0
    && Number(valores.numeroDePagos) >= 2
    && valores.pagosRealizados !== ''
    && Number(valores.pagosRealizados) >= 0
    && Boolean(valores.fechaDelProximoPago)
    && Boolean(valores.idCategoriaDelInteres);

  function peticion() {
    return {
      idCuenta: Number(idCuenta),
      montoOriginal: String(valores.montoOriginal),
      pagoMensual: String(valores.pagoMensual),
      numeroDePagos: Number(valores.numeroDePagos),
      pagosRealizados: Number(valores.pagosRealizados),
      fechaDelProximoPago: valores.fechaDelProximoPago,
      idCategoriaDelInteres: Number(valores.idCategoriaDelInteres),
      fechaLocal: hoy,
    };
  }

  /**
   * LA VISTA PREVIA, PEDIDA AL SERVIDOR (§2, patrón D-78/D-413). Se vuelve a pedir
   * cada vez que cambia un dato que la mueve, así la cifra que se enseña y la que se
   * guardará no pueden diferir: las calcula la misma pieza.
   *
   * SOLO LECTURA GARANTIZADA: cero filas, cero bitácora, idempotente (CA-20-08).
   */
  const recalcular = useCallback(async () => {
    if (!datosCompletos) { setVistaPrevia(null); return; }

    setError(null);
    try {
      setVistaPrevia(await simularPlan(peticion()));
    } catch (fallo) {
      // El rechazo de la simulación se enseña tal cual —es el mismo que daría el
      // guardado, con su texto del servidor— y no se oculta la pantalla.
      setVistaPrevia(null);
      setError(fallo);
    }
    // `peticion` se deriva de `valores`, que ya está en las dependencias.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datosCompletos, valores, idCuenta, hoy]);

  useEffect(() => { recalcular(); }, [recalcular]);

  function cambiar(campo, valor) {
    setValores((previos) => ({ ...previos, [campo]: valor }));
  }

  async function guardar() {
    if (!datosCompletos) return;
    setError(null);
    setGuardando(true);
    try {
      await generarPlan(peticion());
      navegar(`/cuentas/prestamos/${idCuenta}?acuse=plan`, { replace: true });
    } catch (fallo) {
      setError(fallo);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Shell
      titulo="Su plan de pagos"
      volverA={recienCreado ? `/cuentas/prestamos/${idCuenta}` : `/cuentas/prestamos/${idCuenta}`}
    >
      <CintaDeVersionNueva />

      {recienCreado && (
        <div className="aviso ok" role="status" data-prueba="prestamo-plan-acuse-de-alta">
          <b className="tit">Préstamo guardado</b>
          Ya quedó registrado con lo que debes. Falta su plan de pagos: con él aparece en lo que vence
          y lleva el conteo de cada pago. Si sales ahora, el préstamo sigue guardado y podrás
          generarlo después.
        </div>
      )}

      <AvisoDeError error={error} />

      {prestamo && (
        <div className="card" style={{ margin: 12 }} data-prueba="prestamo-plan-encabezado">
          <div className="fila">
            <span className="crece">{prestamo.cuenta.nombre}</span>
            <b>{prestamo.deudaVigente} {codigoISO}</b>
          </div>
          <span className="ayuda">Lo que debes hoy, tal como lo declaraste.</span>
        </div>
      )}

      {prestamo && (
        <>
          <FormularioDelPlan
            valores={valores}
            alCambiar={cambiar}
            codigoISO={codigoISO}
            categorias={categorias}
            vistaPrevia={vistaPrevia}
            hoy={hoy}
            deshabilitarMontoOriginal={recienCreado}
          />

          <div className="card" style={{ margin: 12 }}>
            <button
              className="btn ancho"
              type="button"
              disabled={guardando || !datosCompletos || !vistaPrevia}
              onClick={guardar}
              data-prueba="prestamo-plan-guardar"
            >
              Guardar el plan de pagos
            </button>

            <button
              className="btn sec ancho"
              type="button"
              onClick={() => navegar(`/cuentas/prestamos/${idCuenta}`, { replace: true })}
              data-prueba="prestamo-plan-cancelar"
            >
              {recienCreado ? 'Dejarlo para después' : 'Cancelar'}
            </button>
          </div>
        </>
      )}
    </Shell>
  );
}
